import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ConfirmDialogService,
  ConfirmDialogState,
  ConfirmDialogVariant
} from '../../../services/confirm-dialog.service';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './confirm-dialog.component.html'
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  state: ConfirmDialogState = { isOpen: false, options: { message: '' } };
  private readonly destroy$ = new Subject<void>();

  constructor(private svc: ConfirmDialogService) {}

  ngOnInit(): void {
    this.svc.state$.pipe(takeUntil(this.destroy$)).subscribe(s => this.state = s);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get variant(): ConfirmDialogVariant {
    return this.state.options.variant ?? 'info';
  }

  get hasCancel(): boolean {
    // En modo prompt siempre se muestra Cancelar aunque cancelText esté vacío
    return !!this.state.options.cancelText || !!this.state.prompt;
  }

  get isPrompt(): boolean {
    return !!this.state.prompt;
  }

  get cancelLabel(): string {
    return this.state.options.cancelText || (this.isPrompt ? 'Cancelar' : '');
  }

  get confirmDisabled(): boolean {
    if (!this.state.prompt) return false;
    return this.state.prompt.required !== false && !this.state.prompt.value.trim();
  }

  onPromptInput(value: string): void {
    this.svc.updatePromptValue(value);
  }

  iconBgClass(): string {
    switch (this.variant) {
      case 'danger': return 'bg-error-100 text-error-600 dark:bg-error-500/20 dark:text-error-400';
      case 'warning': return 'bg-warning-100 text-warning-600 dark:bg-warning-500/20 dark:text-warning-400';
      case 'success': return 'bg-success-100 text-success-600 dark:bg-success-500/20 dark:text-success-400';
      case 'info':
      default: return 'bg-brand-100 text-brand-600 dark:bg-brand-500/20 dark:text-brand-400';
    }
  }

  confirmBtnClass(): string {
    switch (this.variant) {
      case 'danger': return 'bg-error-500 hover:bg-error-600';
      case 'warning': return 'bg-warning-500 hover:bg-warning-600';
      case 'success': return 'bg-success-500 hover:bg-success-600';
      case 'info':
      default: return 'bg-brand-500 hover:bg-brand-600';
    }
  }

  iconPath(): string {
    switch (this.variant) {
      case 'danger':
      case 'warning':
        return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z';
      case 'success':
        return 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
      case 'info':
      default:
        return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
    }
  }

  onConfirm(): void {
    if (this.confirmDisabled) return;
    this.svc.resolve(true);
  }

  onCancel(): void {
    if (this.hasCancel) this.svc.resolve(false);
    else this.svc.resolve(true);
  }

  /** Backdrop click: solo cierra cuando hay botón cancelar (modo confirm/prompt). En modo alert, no se descarta por click fuera. */
  onBackdrop(): void {
    if (this.hasCancel) this.svc.resolve(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.state.isOpen) return;
    if (this.hasCancel) this.svc.resolve(false);
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnter(ev: Event): void {
    if (!this.state.isOpen) return;
    const target = ev.target as HTMLElement | null;
    const insideOurDialog = target?.closest('[data-confirm-dialog]') != null;
    if (target?.tagName === 'TEXTAREA' && insideOurDialog) return; // textarea del prompt: Enter inserta salto
    if (target?.tagName === 'TEXTAREA' || (target?.tagName === 'INPUT' && !insideOurDialog) || target?.tagName === 'SELECT') return;
    if (this.confirmDisabled) return;
    ev.preventDefault();
    this.svc.resolve(true);
  }
}
